import i18n from '@/lib/i18n';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * Normalise the active i18next locale into the BCP-47 region-tagged form the
 * backend's RekPlayLocaleResolver expects (`en-CA` / `fr-CA`). i18next stores
 * the short form (`'en'` / `'fr'`) and toggle handlers write that short form,
 * so we expand to the regional code on the way out. Anything unrecognised
 * falls back to en-CA — matches the resolver's platform-default tier.
 */
function acceptLanguageHeader(): string {
  const lang = i18n.language || 'en-CA';
  if (lang.includes('-')) return lang;
  return lang.startsWith('fr') ? 'fr-CA' : 'en-CA';
}

type ApiResponse<T> = {
  data: T;
  meta: { timestamp: string; requestId: string };
};

type ApiError = {
  error: { code: string; message: string; details?: Record<string, unknown> };
};

export type AppError = {
  status: number;
  code: string;
  message: string;
  details?: Record<string, unknown>;
};

let accessToken: string | null = null;
let refreshFn: (() => Promise<string | null>) | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function setRefreshFunction(fn: () => Promise<string | null>) {
  refreshFn = fn;
}

export async function apiClient<T>(
  endpoint: string,
  options: RequestInit & {
    idempotencyKey?: string;
    skipAuth?: boolean;
  } = {}
): Promise<T> {
  const { idempotencyKey, skipAuth, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': acceptLanguageHeader(),
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (!skipAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  let response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  // 401 → try silent refresh, then retry original request
  if (response.status === 401 && refreshFn && !skipAuth) {
    const newToken = await refreshFn();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...fetchOptions,
        headers,
      });
    }
  }

  if (!response.ok) {
    const errorBody: ApiError = await response.json().catch(() => ({
      error: { code: 'NETWORK_ERROR', message: 'Connection failed' },
    }));
    const appError: AppError = {
      status: response.status,
      code: errorBody.error?.code || 'UNKNOWN_ERROR',
      message: errorBody.error?.message || 'An error occurred',
      details: errorBody.error?.details,
    };
    throw appError;
  }

  const result: ApiResponse<T> = await response.json();
  return result.data;
}
